// On a phone (a finger is the main pointer) the body opens in a "reading" state:
// a long press is a plain text selection that brings up no keyboard, and a light
// tap starts writing.
//
// Why: a reading body cannot be written (its root is contenteditable="false",
// the editor's ReadingMode), the same as a chat message. Left writable, iPhone
// put the focus into the body on a long press and, to show the focused field,
// moved the page until the chosen word reached the bottom; the finger had not
// moved, so the selection grew to it. `inputmode="none"`, scrolling back and
// focusing first did not help.
//
// - Reading: when made, and after the keyboard closes. A long press selects
//   text; ProseMirror still reads the selection, so the formatting bar appears
//   (its buttons work without the focus).
// - A tap while text is selected only clears the selection.
// - Writing: a short tap that hardly moved. The focus goes in on the click that
//   follows (iOS raises the keyboard only for a focus inside a click), with the
//   caret where the tap was.
//
// While reading `editor.isEditable` is false; parts that treat the body as
// writable anyway (the formatting bar, the block menu) use `canEdit`. Mouse
// devices have no reading state.
import { type Editor, Extension } from "@tiptap/core";
import { isReadingMode, ReadingMode, setReadingMode } from "@trameria/editor";

/** Shorter than iOS's long press (500 ms). */
const TAP_MAX_MS = 400;
/** Moving more than this between down and up is a scroll. */
const TAP_SLOP_PX = 10;
/** The visible height shrinking by this much means the keyboard is up. */
const KEYBOARD_MIN_PX = 120;
/** Taps on these do their own thing and do not start writing. */
const NOT_TEXT =
  'a, button, input, label, img, video, [contenteditable="false"]';

/** Whether the main pointer is a finger (a touch laptop is not). */
export function touchFirst(): boolean {
  return (
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(hover: none) and (pointer: coarse)").matches
  );
}

/** Leaves the reading state (the caller puts the focus in). */
export function startTyping(editor: Editor): void {
  setReadingMode(editor, false);
}

export function isReading(editor: Editor): boolean {
  return isReadingMode(editor);
}

/** A body that can be written, reading or not. */
export function canEdit(editor: Editor): boolean {
  return editor.isEditable || (isReading(editor) && editor.options.editable);
}

function attach(editor: Editor): () => void {
  const dom = editor.view.dom;
  // Made with the focus inside (tapped before it was made): writing already.
  if (!dom.contains(document.activeElement)) setReadingMode(editor, true);

  const selecting = () => {
    const s = document.getSelection();
    return (
      !!s &&
      !s.isCollapsed &&
      s.rangeCount > 0 &&
      dom.contains(s.getRangeAt(0).commonAncestorContainer)
    );
  };
  let press: {
    id: number;
    x: number;
    y: number;
    at: number;
    selecting: boolean;
  } | null = null;
  let tapped: { pos: number; at: number } | null = null;

  const onDown = (e: PointerEvent) => {
    press =
      e.pointerType === "touch"
        ? {
            id: e.pointerId,
            x: e.clientX,
            y: e.clientY,
            at: Date.now(),
            selecting: isReading(editor) && selecting(),
          }
        : null;
  };
  const onUp = (e: PointerEvent) => {
    const p = press;
    press = null;
    if (!p || e.pointerId !== p.id || !isReading(editor)) return;
    if (Date.now() - p.at > TAP_MAX_MS) return;
    if (
      Math.abs(e.clientX - p.x) > TAP_SLOP_PX ||
      Math.abs(e.clientY - p.y) > TAP_SLOP_PX
    )
      return;
    if (p.selecting) {
      document.getSelection()?.removeAllRanges();
      return;
    }
    if (!editor.options.editable) return;
    // The reading root is contenteditable="false" too: only look inside it.
    const hit = e.target instanceof Element ? e.target.closest(NOT_TEXT) : null;
    if (hit && hit !== dom && dom.contains(hit)) return;
    const at = editor.view.posAtCoords({ left: e.clientX, top: e.clientY });
    if (at) tapped = { pos: at.pos, at: Date.now() };
  };
  const onClick = () => {
    const t = tapped;
    tapped = null;
    if (!t || Date.now() - t.at > TAP_MAX_MS || !isReading(editor)) return;
    startTyping(editor);
    editor.chain().setTextSelection(t.pos).focus().run();
  };
  const onCancel = () => {
    press = null;
  };

  // Back to reading when the keyboard closes: the visible height is back at the
  // tallest seen (taken again when the width changes: the phone turned).
  const vv = window.visualViewport;
  let tallest = vv ? vv.height * vv.scale : 0;
  let width = vv?.width ?? 0;
  let open = false;
  const onResize = () => {
    if (!vv) return;
    const height = vv.height * vv.scale;
    if (vv.width !== width) {
      width = vv.width;
      tallest = height;
    }
    tallest = Math.max(tallest, height);
    const now = height < tallest - KEYBOARD_MIN_PX;
    if (open && !now) setReadingMode(editor, true);
    open = now;
  };

  dom.addEventListener("pointerdown", onDown);
  dom.addEventListener("pointerup", onUp);
  dom.addEventListener("pointercancel", onCancel);
  dom.addEventListener("click", onClick);
  vv?.addEventListener("resize", onResize);
  return () => {
    dom.removeEventListener("pointerdown", onDown);
    dom.removeEventListener("pointerup", onUp);
    dom.removeEventListener("pointercancel", onCancel);
    dom.removeEventListener("click", onClick);
    vv?.removeEventListener("resize", onResize);
  };
}

export const TapToType = Extension.create<
  Record<string, never>,
  { detach: (() => void) | null }
>({
  name: "trameriaTapToType",

  addExtensions() {
    return [ReadingMode];
  },

  addStorage() {
    return { detach: null };
  },

  onCreate() {
    if (!touchFirst()) return;
    this.storage.detach = attach(this.editor);
  },

  onDestroy() {
    this.storage.detach?.();
    this.storage.detach = null;
  },
});
